import React, { useEffect, useState } from 'react';
import { Edit, Plus, RefreshCw, Trash2, Search, Filter, Calendar, MessageSquare, Star, User, CheckCircle, X, ChevronLeft, ChevronRight, Info, FileText, Target, Award, BookOpen, GraduationCap } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const EntretiensCarriere = () => {
  const { user } = useAuth();
  const { filialeId, currentFiliale } = useFiliale();
  const [entretiens, setEntretiens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    personnel: [],
    managers: []
  });
  const [formData, setFormData] = useState({
    personnel_id: '',
    manager_id: '',
    filiale_id: filialeId || '',
    type_entretien: 'annuel',
    date_entretien: '',
    objectifs_precedents: '',
    resultats_atteints: '',
    objectifs_futurs: '',
    aspirations_employe: '',
    opportunites_disponibles: '',
    plan_developpement: '',
    commentaires_manager: '',
    note_performance: '',
    signature_employe: 0,
    signature_manager: 0
  });
  const [filters, setFilters] = useState({
    personnel_id: '',
    type_entretien: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(1);

  useEffect(() => {
    fetchEntretiens();
    fetchDropdownData();
  }, [filialeId]);

  const fetchEntretiens = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      if (filters.personnel_id) {
        params.personnel_id = filters.personnel_id;
      }
      if (filters.type_entretien) {
        params.type_entretien = filters.type_entretien;
      }
      
      const queryString = new URLSearchParams(params).toString();
      const response = await http.get(`/api/entretiens-carriere${queryString ? '?' + queryString : ''}`);
      setEntretiens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement entretiens:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const personnelUrl = (filialeId && filialeId !== 'all') 
        ? `/api/personnel?filiale_id=${filialeId}` 
        : '/api/personnel';
      
      const [personnel, managers] = await Promise.all([
        http.get(personnelUrl),
        http.get('/api/personnel/managers')
      ]);

      const personnelArray = Array.isArray(personnel.data) ? personnel.data : 
                             (personnel.data && personnel.data.data && Array.isArray(personnel.data.data) ? personnel.data.data : []);

      const managersArray = Array.isArray(managers.data) ? managers.data : 
                            (managers.data && managers.data.data && Array.isArray(managers.data.data) ? managers.data.data : []);

      setDropdownData({
        personnel: personnelArray,
        managers: managersArray
      });
    } catch (error) {
      console.error('Erreur chargement dropdown:', error);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setActiveTab(1);
    setFormData({
      personnel_id: '',
      manager_id: '',
      filiale_id: filialeId || '',
      type_entretien: 'annuel',
      date_entretien: '',
      objectifs_precedents: '',
      resultats_atteints: '',
      objectifs_futurs: '',
      aspirations_employe: '',
      opportunites_disponibles: '',
      plan_developpement: '',
      commentaires_manager: '',
      note_performance: '',
      signature_employe: 0,
      signature_manager: 0
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setActiveTab(1);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet entretien ?')) {
      return;
    }
    try {
      await http.delete(`/api/entretiens-carriere/${id}`);
      fetchEntretiens();
    } catch (error) {
      console.error('Erreur suppression entretien:', error);
      alert('Erreur lors de la suppression de l\'entretien');
    }
  };

  const handleSigner = async (id, role) => {
    try {
      await http.put(`/api/entretiens-carriere/${id}/signer/${role}`);
      fetchEntretiens();
    } catch (error) {
      console.error('Erreur signature:', error);
      alert('Erreur lors de la signature');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/entretiens-carriere/${editingItem.id}`, formData);
      } else {
        await http.post('/api/entretiens-carriere', formData);
      }
      setShowModal(false);
      fetchEntretiens();
    } catch (error) {
      console.error('Erreur sauvegarde entretien:', error);
      alert('Erreur lors de la sauvegarde de l\'entretien');
    }
  };

  // Filtrage et pagination
  const filteredEntretiens = entretiens.filter(e => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (e.prenom && e.prenom.toLowerCase().includes(searchLower)) ||
      (e.nom && e.nom.toLowerCase().includes(searchLower)) ||
      (e.type_entretien && e.type_entretien.toLowerCase().includes(searchLower))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEntretiens = filteredEntretiens.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEntretiens.length / itemsPerPage);

  const getTypeColor = (type) => {
    const colors = {
      'annuel': 'bg-blue-100 text-blue-800',
      'professionnel': 'bg-purple-100 text-purple-800',
      'evaluation_360': 'bg-green-100 text-green-800',
      'sortie': 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'annuel': <Calendar className="h-4 w-4" />,
      'professionnel': <MessageSquare className="h-4 w-4" />,
      'evaluation_360': <Star className="h-4 w-4" />,
      'sortie': <User className="h-4 w-4" />
    };
    return icons[type] || <FileText className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Entretiens Carrière"
        description="Gestion des entretiens annuels, professionnels et évaluations"
        icon={MessageSquare}
        actions={[
          {
            label: 'Nouvel entretien',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchEntretiens,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filtres et recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-700">Filtres</h3>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
              <select
                value={filters.personnel_id}
                onChange={(e) => setFilters({ ...filters, personnel_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                {dropdownData.personnel.map(p => (
                  <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'entretien</label>
              <select
                value={filters.type_entretien}
                onChange={(e) => setFilters({ ...filters, type_entretien: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                <option value="annuel">Annuel</option>
                <option value="professionnel">Professionnel</option>
                <option value="evaluation_360">Évaluation 360°</option>
                <option value="sortie">Sortie</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau des entretiens */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">
              Chargement...
            </div>
          ) : currentEntretiens.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">
              Aucun entretien trouvé.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employé</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Manager</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Note</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Signatures</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentEntretiens.map((entretien) => (
                      <tr key={entretien.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {entretien.prenom} {entretien.nom}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {entretien.manager_prenom} {entretien.manager_nom}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(entretien.type_entretien)}`}>
                            {getTypeIcon(entretien.type_entretien)}
                            {entretien.type_entretien.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {new Date(entretien.date_entretien).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {entretien.note_performance ? (
                            <span className="inline-flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500 fill-current" />
                              {entretien.note_performance}/5
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            {entretien.signature_employe ? (
                              <CheckCircle className="h-4 w-4 text-green-600" title="Signé employé" />
                            ) : (
                              <X className="h-4 w-4 text-gray-400" title="Non signé employé" />
                            )}
                            {entretien.signature_manager ? (
                              <CheckCircle className="h-4 w-4 text-green-600" title="Signé manager" />
                            ) : (
                              <X className="h-4 w-4 text-gray-400" title="Non signé manager" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(entretien)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleSigner(entretien.id, 'employe')}
                              className="text-green-600 hover:text-green-800"
                              title="Signer employé"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleSigner(entretien.id, 'manager')}
                              className="text-purple-600 hover:text-purple-800"
                              title="Signer manager"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(entretien.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredEntretiens.length)} sur {filteredEntretiens.length} entretiens
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Entretien Carrière */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-5xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingItem ? 'Modifier' : 'Créer'} Entretien Carrière
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-900"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Onglets */}
              <div className="border-b bg-white">
                <div className="flex">
                  <button
                    key="tab-1"
                    onClick={() => setActiveTab(1)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 1
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Informations
                  </button>
                  <button
                    key="tab-2"
                    onClick={() => setActiveTab(2)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 2
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Objectifs
                  </button>
                  <button
                    key="tab-3"
                    onClick={() => setActiveTab(3)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 3
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Aspirations
                  </button>
                  <button
                    key="tab-4"
                    onClick={() => setActiveTab(4)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 4
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Développement
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Onglet 1: Informations */}
                  {activeTab === 1 && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Informations générales</p>
                          <p>Sélectionnez l'employé, le manager et le type d'entretien. Définissez la date de l'entretien et évaluez la performance globale.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Employé *</label>
                          <select
                            value={formData.personnel_id}
                            onChange={(e) => setFormData({ ...formData, personnel_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.personnel.map(p => (
                              <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Manager *</label>
                          <select
                            value={formData.manager_id}
                            onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.managers.map(m => (
                              <option key={m.idpersonnel} value={m.idpersonnel}>{m.prenom} {m.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type d'entretien *</label>
                          <select
                            value={formData.type_entretien}
                            onChange={(e) => setFormData({ ...formData, type_entretien: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="annuel">Annuel</option>
                            <option value="professionnel">Professionnel</option>
                            <option value="evaluation_360">Évaluation 360°</option>
                            <option value="sortie">Sortie</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date d'entretien *</label>
                          <input
                            type="date"
                            value={formData.date_entretien}
                            onChange={(e) => setFormData({ ...formData, date_entretien: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Note de performance (sur 5)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="5"
                            value={formData.note_performance}
                            onChange={(e) => setFormData({ ...formData, note_performance: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Ex: 4.5"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 2: Objectifs */}
                  {activeTab === 2 && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-purple-800">
                          <p className="font-semibold mb-1">Objectifs et résultats</p>
                          <p>Revue des objectifs précédents et définition des nouveaux objectifs pour la période à venir.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Target className="h-4 w-4 text-blue-600" />
                            Objectifs précédents
                          </label>
                          <textarea
                            value={formData.objectifs_precedents}
                            onChange={(e) => setFormData({ ...formData, objectifs_precedents: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Objectifs fixés lors du dernier entretien..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Award className="h-4 w-4 text-green-600" />
                            Résultats atteints
                          </label>
                          <textarea
                            value={formData.resultats_atteints}
                            onChange={(e) => setFormData({ ...formData, resultats_atteints: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Bilan des objectifs atteints et non atteints..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Star className="h-4 w-4 text-yellow-600" />
                            Objectifs futurs
                          </label>
                          <textarea
                            value={formData.objectifs_futurs}
                            onChange={(e) => setFormData({ ...formData, objectifs_futurs: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Nouveaux objectifs pour la période à venir..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 3: Aspirations */}
                  {activeTab === 3 && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-green-800">
                          <p className="font-semibold mb-1">Aspirations et opportunités</p>
                          <p>Discussion sur les aspirations professionnelles de l'employé et les opportunités disponibles.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <User className="h-4 w-4 text-purple-600" />
                            Aspirations de l'employé
                          </label>
                          <textarea
                            value={formData.aspirations_employe}
                            onChange={(e) => setFormData({ ...formData, aspirations_employe: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Projets professionnels, souhaits d'évolution, mobilité..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Target className="h-4 w-4 text-orange-600" />
                            Opportunités disponibles
                          </label>
                          <textarea
                            value={formData.opportunites_disponibles}
                            onChange={(e) => setFormData({ ...formData, opportunites_disponibles: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Postes ouverts, projets à venir, formations disponibles..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 4: Développement */}
                  {activeTab === 4 && (
                    <div className="space-y-4">
                      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-cyan-800">
                          <p className="font-semibold mb-1">Plan de développement</p>
                          <p>Actions concrètes pour soutenir le développement professionnel de l'employé.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <BookOpen className="h-4 w-4 text-green-600" />
                            Plan de développement
                          </label>
                          <textarea
                            value={formData.plan_developpement}
                            onChange={(e) => setFormData({ ...formData, plan_developpement: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Formations, mentorat, projets spécifiques..."
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <MessageSquare className="h-4 w-4 text-blue-600" />
                            Commentaires du manager
                          </label>
                          <textarea
                            value={formData.commentaires_manager}
                            onChange={(e) => setFormData({ ...formData, commentaires_manager: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="4"
                            placeholder="Synthèse, recommandations, points de vigilance..."
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.max(1, activeTab - 1))}
                    disabled={activeTab === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.min(4, activeTab + 1))}
                    disabled={activeTab === 4}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingItem ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntretiensCarriere;
